<script>
import { mapGetters } from 'vuex';
import { get, filter, orderBy } from 'lodash';
import qs from 'query-string';
import { planTypes, OPC_HREF_SYNC_QUERY_PARAMS } from '@/common/constants';
import { updateHrefWithQueryParams } from '@/utils';
import { getJointSaleProBundleCode } from '@/plans/ZOOM_PHONE/helper/utils';
import base from '@/views/config/addon-cards/templates/base.vue';
import logo from '@/assets/plans/icon_ZM.svg';
import { AddOnGroupTrackingUILocation, getTrackingFlowType } from '@/aspects/trackings';
import { translateToEn } from '@/plugins/i18n';
import { getAllPlanPrice } from '@/apis';
import { getCurrency } from '@/utils/currency';
import { navigateCurrentWindowTo } from '@/utils/modal-utils.js';

export default {
  name: 'ProPlusAddOnCard',
  mixins: [base],
  data() {
    return {
      planType: planTypes.ZONE_PRO_PLUS,
      title: this.$t('billing.zone_pro_plus.title'),
      trackingPlanName: translateToEn('billing.zone_pro_plus.title'),
      subtitle: '',
      logo,
      description: this.$t('billing.zoom_one_pro.desc'),
      addText: this.$t('common.btn_switch_plan'),
      addTrackingText: translateToEn('common.btn_switch_plan'),
    };
  },
  computed: {
    ...mapGetters([
      'planMapFlat',
      'getCurrentBasePlanInfo',
      'ipLocation',
      'discountWithAmountMap',
      'couponState'
    ]),
    currentProductPlans() {
      return orderBy(
        filter(this.planMapFlat, (p) => p.type === this.planType && !p.isOneMonth),
        ['participants', 'amount'],
        'asc',
      );
    },
    currentBasePlanCodes() {
      return this.getCurrentBasePlanInfo.plans.map(p => p.plan_code).join(',');
    },
    currentIpGeoCountryCode() {
      return get(this.ipLocation, 'cn');
    },
    isProPlusSoftBundle() {
      return this.currentIpGeoCountryCode !== 'US' && this.currentIpGeoCountryCode !== 'CA';
    },
  },
  methods: {
    switchToSoftProPlusBundle() {
      const jointsale = getJointSaleProBundleCode(this.currentIpGeoCountryCode);
      const cacheTimestamp = qs.parse(location.search)._;
      navigateCurrentWindowTo(
        updateHrefWithQueryParams('/opc/buy/config', OPC_HREF_SYNC_QUERY_PARAMS, {
          type: this.$context.flow,
          jointsale,
          switchFrom: `${this.currentBasePlanCodes},${cacheTimestamp}`,
        }),
        { replace: true }
      );
    },
    switchToProPlus() {
      const cacheTimestamp = qs.parse(location.search)._;
      const params = {
        type: this.$context.flow,
        switchFrom: `${this.currentBasePlanCodes},${cacheTimestamp}`,
      };
      if (this.couponState.couponCode) {
        params.persistCoupon = this.couponState.couponCode;
      }
      navigateCurrentWindowTo(
        updateHrefWithQueryParams(
          '/opc/buy?plan=zoneproplus&from=zoneproplus_zonepropzvu&period=annual',
          OPC_HREF_SYNC_QUERY_PARAMS,
          params,
        ),
        { replace: true }
      );
    },
    // switch to zoom workplace pro plus
    async addOn() {
      this.$tracking({
        event: 'buyInteraction',
        buyInteraction: `${this.trackingBasePlanName} add-ons`,
        buySelection: `${this.trackingPlanName} - ${this.trackingAddAction}`,
        uiLocation: AddOnGroupTrackingUILocation[this.group],
        flowType: getTrackingFlowType(),
      });
      if (!this.isProPlusSoftBundle) {
        this.switchToProPlus();
        return;
      }

      if (this.discountWithAmountMap?.totalDiscount > 0) {
        this.$tracking.buyInteraction('checkout options', 'coupon replacement modal - impression');
        this.$confirm(
          this.$t('billing.coupon.switch_to_soft_bundle_notify.desc'),
          this.$t('billing.coupon.switch_to_soft_bundle_notify.title'),
          {
            roundButton: true,
            confirmButtonText: this.$t('common.btn_continue'),
            cancelButtonText: this.$t('common.btn_cancel'),
          },
        )
          .then(() => {
            this.$tracking.buyInteraction('checkout options', 'coupon replacement modal - continue');
            this.switchToSoftProPlusBundle();
          })
          .catch(() => {
            this.$tracking.buyInteraction('checkout options', 'coupon replacement modal - cancel');
          });
      } else {
        this.switchToSoftProPlusBundle();
      }
    },
  },
  created() {
    const { amount } = get(this.currentProductPlans, [0], {});
    this.subtitle = this.$t('billing.common.form_xx_month', [this.$money(amount)]);
    this.productPlans = this.currentProductPlans;
    // pro plus soft bundle only for non-us/ca
    if (this.isProPlusSoftBundle) {
      getAllPlanPrice()
        .then(({ result }) => {
          const currency = getCurrency();
          const jointsale = getJointSaleProBundleCode(this.currentIpGeoCountryCode);
          const planKey = `JS_${jointsale}_monthly`;
          const price = get(result, [currency, planKey]);
          this.subtitle = this.$t('billing.common.form_xx_month', [this.$money(price)]);
        })
        .catch(() => {
          console.error('Call getAllPlanPrice error');
          this.subtitle = '';
        });
      // reset planType to empty string to hide the learn more button
      this.planType = '';
    }
  },
};
</script>
